// The Award tab of an awarded event (Lane 1395): the saved award only (never recalculated),
// its download for setting up POs, and each supplier's notice with its email state.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import StatTile from './StatTile';
import { AwardTiles } from './AwardReview';
import { saveBlob } from './DocumentsTab';
import { formatAmount, formatUnitPrice, formatUnits, newKey } from './sourcingApi';

const KIND_TEXT = { award_won: 'Awarded parts', award_kept: 'Keeps today’s parts', award_not_won: 'Not awarded' };
const STATE_TEXT = { sent: 'Sent', pending: 'Waiting to send', sending: 'Sending', failed: 'Failed',
  email_off: 'Email is off: in the portal only' };

// Reopen the award (Lane 1395 QA, Ryan): the award stays as history, the event becomes
// editable again, and suppliers are told nothing until the next award.
function ReopenBox({ api, eventId, revision, awardId, onDone, onCancel }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const key = useRef(newKey());
  const reopen = async () => {
    setBusy(true);
    setError('');
    try {
      await api.reopenAward(eventId, revision, awardId, reason.trim(), key.current);
      await onDone();
    } catch (e) {
      setError(e.code === 'award_changed' || e.code === 'stale_revision'
        ? 'The award changed since you opened it. Reload the page.' : e.message);
    } finally { setBusy(false); }
  };
  return (
    <section className="srce-card2" data-testid="award-reopen">
      <div className="srce-card2-f srce-stack">
        <span className="srce-strong">Reopen the award?</span>
        <span>The event becomes editable again: bids, rules, scenarios and rounds. This award stays in the event's history. Suppliers are not told; they hear only when you award again.</span>
        <label htmlFor="reopen-why" className="srce-mut">Reason (optional, your team only)</label>
        <textarea id="reopen-why" className="srce-input" rows={2} maxLength={2000} value={reason}
          onChange={(e) => { setReason(e.target.value); key.current = newKey(); }} />
        {error && <div className="srce-error" role="alert">{error}</div>}
        <span className="srce-row">
          <button type="button" className="srce-btn" disabled={busy} onClick={onCancel}>Cancel</button>
          <button type="button" className="srce-btn srce-btn--primary" disabled={busy} onClick={reopen}>
            {busy ? 'Reopening…' : 'Reopen the award'}</button>
        </span>
      </div>
    </section>
  );
}

export default function AwardTab({ api, eventId, refreshKey, revision, onReopened }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // The award and revision the confirm box was opened for (Codex reopen code #1): a
  // refresh that brings a newer award or revision closes the box, never retargets it.
  const [reopening, setReopening] = useState(null);
  const load = useCallback(() => api.awardView(eventId).then(setData).catch((e) => setError(e.message)),
    [api, eventId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    if (!reopening || !data) return;
    if (data.award_id !== reopening.awardId || revision !== reopening.revision) {
      setReopening(null);
      setError('The award or the event changed while you were reopening it. Look again before you reopen.');
    }
  }, [data, revision, reopening]);
  if (error && !data) return <div className="srce-error" role="alert">{error}</div>;
  if (!data) return <div className="ll-loading">Loading the award…</div>;
  const currency = data.currency || 'USD';
  const t = data.totals;
  const download = () => api.downloadAward(eventId)
    .then((b) => saveBlob(b, `award-${eventId.slice(0, 8)}.xlsx`)).catch((e) => setError(e.message));
  const resend = async () => {
    setBusy(true);
    setError('');
    try { await api.resendAwardNotices(eventId); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="srce-ov srce-award" data-testid="award-tab">
      <AwardTiles totals={t} currency={currency} awardLabel="Awarded" fourth={
        <StatTile label="Awarded by" value={data.awarded_by || 'A buyer'}
          note={`${new Date(data.awarded_at).toLocaleDateString()}, scenario #${data.scenario_number || ''}`} />} />
      {error && <div className="srce-error" role="alert">{error}</div>}
      {reopening && (
        <ReopenBox api={api} eventId={eventId} revision={reopening.revision} awardId={reopening.awardId}
          onDone={onReopened} onCancel={() => setReopening(null)} />
      )}
      <section className="srce-card2">
        <div className="srce-card2-h">
          <span className="srce-card2-t">By supplier</span>
          <span className="srce-push srce-row">
            <button type="button" className="srce-btn" onClick={download}>Download for POs (Excel)</button>
            {onReopened && revision && !reopening && (
              <button type="button" className="srce-btn"
                onClick={() => { setError(''); setReopening({ awardId: data.award_id, revision }); }}>Reopen the award</button>
            )}
          </span>
        </div>
        <div className="srce-scroll">
          <table className="srce-sc-tbl">
            <thead><tr><th>Supplier</th><th className="srce-num">Lines</th><th className="srce-num">Today</th>
              <th className="srce-num">Awarded</th><th className="srce-num">Change</th><th>Note</th></tr></thead>
            <tbody>
              {data.by_supplier.map((s) => {
                const c = Number(s.change_known);
                return (
                  <tr key={s.supplier_id}>
                    <td>{s.supplier_name}</td><td className="srce-num">{s.lines}</td>
                    <td className="srce-num">{s.today_known === null ? 'No price today' : formatAmount(s.today_known, currency, 0)}</td>
                    <td className="srce-num">{formatAmount(s.quoted, currency, 0)}</td>
                    <td className={`srce-num${c > 0 ? ' srce-good' : c < 0 ? ' srce-neg' : ''}`}>
                      {s.change_known === null ? '-' : c >= 0 ? `${formatAmount(c, currency, 0)} saved` : `${formatAmount(-c, currency, 0)} more`}</td>
                    <td className="srce-warn-text">{s.qualification_pending ? 'Subject to qualification' : ''}</td>
                  </tr>
                );
              })}
              {data.no_change.length > 0 && (
                <tr className="srce-mut"><td>No change (stays as today)</td><td className="srce-num">{data.no_change.length}</td>
                  <td className="srce-num">{formatAmount(t.kept_as_today, currency, 0)}</td>
                  <td className="srce-num">{formatAmount(t.kept_as_today, currency, 0)}</td><td className="srce-num">-</td><td /></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      <section className="srce-card2">
        <div className="srce-card2-h"><span className="srce-card2-t">Lines</span></div>
        <div className="srce-scroll">
          <table className="srce-sc-tbl">
            <thead><tr><th>Part</th><th>Description</th><th>Site</th><th>Supplier</th><th className="srce-num">Quantity</th>
              <th className="srce-num">Price</th><th className="srce-num">Cost</th><th>Note</th></tr></thead>
            <tbody>
              {data.award.map((x) => (
                <tr key={`${x.line_uid}-${x.supplier_id}`}>
                  <td>{x.part}</td><td title={x.description || ''}>{x.description || ''}</td><td>{x.site || ''}</td>
                  <td>{x.supplier_name}</td><td className="srce-num">{formatUnits(x.units)}</td>
                  <td className="srce-num">{formatUnitPrice(x.price, currency)}</td>
                  <td className="srce-num">{formatAmount(x.cost, currency)}</td>
                  <td className="srce-warn-text">{x.qualification_pending ? 'Subject to qualification' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="srce-card2">
        <div className="srce-card2-h">
          <span className="srce-card2-t">Notices</span>
          {data.can_resend && (
            <span className="srce-push"><button type="button" className="srce-btn" disabled={busy} onClick={resend}>
              {busy ? 'Sending…' : 'Send again'}</button></span>
          )}
        </div>
        <div className="srce-scroll">
          <table className="srce-sc-tbl">
            <thead><tr><th>Supplier</th><th>Notice</th><th>Email</th></tr></thead>
            <tbody>
              {data.notices.length === 0 && <tr><td colSpan={3} className="srce-mut">No supplier had joined, so no one was told.</td></tr>}
              {data.notices.map((n, i) => (
                <tr key={`${n.supplier_id}-${i}`}><td>{n.supplier_name}</td><td>{KIND_TEXT[n.kind] || n.kind}</td>
                  <td className={n.state === 'failed' ? 'srce-neg' : n.state === 'sent' ? 'srce-good' : ''}>{STATE_TEXT[n.state] || n.state}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {data.rationale && (
        <section className="srce-card2">
          <div className="srce-card2-h"><span className="srce-card2-t">Why (your team only)</span></div>
          <div className="srce-card2-f">{data.rationale}</div>
        </section>
      )}
    </div>
  );
}
